/**
 * The game's own map marker icons.
 *
 * Mirrored from the in-game UI so this map reads the same way the in-game map
 * does. A player glancing at a second screen should not have to translate
 * between two visual languages — a tower is the tower icon, an artillery
 * position is the artillery icon, and nothing needs a legend.
 *
 * Assets are (c) BULKHEAD / Team17. See scripts/fetch-markers.mjs.
 */

export const MARKER_BASE_PATH = "/maps/markers";

/** Every icon shipped by the game's map UI. */
export type MarkerIcon =
  // Objectives and structures
  | "fob"
  | "tower"
  | "spawn_board"
  | "spawn_vehicle"
  | "weapons_vendor"
  | "garage_vendor"
  // Factions
  | "lonestar"
  | "manticore"
  | "valkyra"
  // Roles
  | "assault"
  | "driver"
  | "medic"
  | "pilot"
  | "recon"
  | "support"
  // Vehicles
  | "quad"
  | "kodiak"
  | "dune"
  | "ural"
  | "tank"
  | "havoc"
  | "heli"
  | "artillery"
  // Callouts
  | "spotted"
  | "warning"
  | "hill_warning"
  | "lasthit"
  | "like"
  // Branding
  | "wardogs"
  | "bulkhead";

export function markerUrl(icon: MarkerIcon): string {
  return `${MARKER_BASE_PATH}/${icon}.webp`;
}

/**
 * The subset of icons a player can actually drop on the map, as opposed to
 * the full `MarkerIcon` set, which also covers fixed map furniture (towers,
 * vendors) and per-player role badges that are never placed by hand.
 *
 * This is also what `session/schemas.ts`'s `MarkerKindSchema` is built from —
 * one list, so the palette offered here and the value that gets validated
 * and synced can never drift apart.
 */
export type PlaceableMarkerIcon =
  | "spotted"
  | "warning"
  | "hill_warning"
  | "lasthit"
  | "like"
  | "fob"
  | "artillery"
  | "spawn_vehicle"
  | "quad"
  | "dune"
  | "kodiak"
  | "ural"
  | "tank"
  | "havoc"
  | "heli"
  | "lonestar"
  | "valkyra"
  | "manticore";

/**
 * Icons a player can drop on the map themselves, grouped the way the in-game
 * marker wheel groups them. Vendors, spawn boards and towers are deliberately
 * absent: those are fixed features of the map, not things you place.
 */
export interface MarkerChoice {
  readonly icon: PlaceableMarkerIcon;
  readonly label: string;
  /** True for enemy-position markers — same icon, drawn as a threat. */
  readonly threat?: true;
}

export interface MarkerGroup {
  readonly id: string;
  readonly label: string;
  readonly choices: readonly MarkerChoice[];
}

export const MARKER_GROUPS: readonly MarkerGroup[] = [
  {
    id: "callouts",
    label: "Callouts",
    choices: [
      { icon: "spotted", label: "Spotted" },
      { icon: "warning", label: "Warning" },
      { icon: "hill_warning", label: "High ground" },
      { icon: "lasthit", label: "Last hit" },
      { icon: "like", label: "Good spot" },
    ],
  },
  {
    id: "assets",
    label: "Assets",
    choices: [
      { icon: "fob", label: "FOB" },
      { icon: "artillery", label: "Artillery" },
      { icon: "spawn_vehicle", label: "Vehicle spawn" },
    ],
  },
  {
    id: "vehicles",
    label: "Vehicles",
    choices: [
      { icon: "quad", label: "Quad" },
      { icon: "dune", label: "Dune" },
      { icon: "kodiak", label: "Kodiak" },
      { icon: "ural", label: "Ural" },
      { icon: "tank", label: "Tank" },
      { icon: "havoc", label: "Havoc" },
      { icon: "heli", label: "Helicopter" },
    ],
  },
  {
    id: "factions",
    label: "Factions",
    choices: [
      { icon: "lonestar", label: "Lonestar" },
      { icon: "valkyra", label: "Valkyra" },
      { icon: "manticore", label: "Manticore" },
    ],
  },
  {
    id: "threats",
    label: "Enemy",
    choices: [
      { icon: "artillery", label: "Enemy mortar", threat: true },
      { icon: "artillery", label: "Enemy SPH", threat: true },
      { icon: "spotted", label: "Enemy spotted", threat: true },
      { icon: "warning", label: "Danger zone", threat: true },
    ],
  },
];
